/* eslint-disable react/jsx-wrap-multilines */
import { FC } from 'react';
import { State } from 'redux/initialState';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';
import { format } from 'date-fns';

import { Document, Operation, Society, Transaction } from 'types';

import { dateFormat } from 'constants/formats';
import operationTypes from 'constants/operationTypes';

import { formatCurrencyDecimals, formatNumber } from 'utils/formats';

import IconTooltip from 'modules/_shared/components/Icon/IconTooltip';
import DocumentsColumn from 'modules/_shared/components/DocumentsColumn';
import Button from 'modules/_shared/components/Buttons';

import { generateSharesTitlesFilename } from 'modules/partners/utils/generateSharesTitlesFilename';
import { useGetOperationTitleData } from '../hooks/useGetOperationTitleData';
import { usePartnerOperations } from '../context/PartnerOperationsContext';
import { useGetOperationData } from '../hooks/useGetOperationData';
import PartnerTransactionRow from './PartnerTransactionRow';
import { ShareTitleTooltipText } from '../../PartnerSociety/components/ShareTitleTooltip';

type PartnerOperationsTableRowProps = {
  index: number;
  operation: Operation;
};

const PartnerOperationsTableRow: FC<PartnerOperationsTableRowProps> = ({
  index,
  operation,
}) => {
  const { t } = useTranslate();

  const actualSociety: Society = useSelector(
    (state: State) => state.society.actualSociety
  );

  const { data, actions } = usePartnerOperations();

  const rowNumber =
    data.totalOperations - ((data.page - 1) * data.rowsPerPage + index);

  const { data: operationData } = useGetOperationData({
    partnerId: data.partner?._id,
    operation,
    nominalValue: actualSociety?.nominalValue || 0,
  });

  const { data: titleData } = useGetOperationTitleData({
    sharesTitles: data.sharesTitles,
    operation,
  });

  const downloadTitleName = titleData
    ? generateSharesTitlesFilename({
        sharesTitle: { number: titleData.titleNumber },
        t,
      })
    : '';

  const societyShareTitleConfiguration =
    actualSociety?.configuration?.sharesTitles;

  const handleExpandRow = () => {
    actions.handleExpandRow(rowNumber);
  };

  return (
    <>
      <tr className="nk-tb-item">
        <td
          key={`row-number-${index}`}
          className="nk-tb-col text-center vertical-align-middle"
        >
          <b>{rowNumber}</b>
        </td>

        <td className="nk-tb-col text-center vertical-align-middle">
          {operationData.showExpandButton ? (
            <Button className="badge btn-light" onClick={handleExpandRow}>
              <em
                className={`icon ni ni-chevron-${
                  data.expandedRows.has(rowNumber) ? 'up' : 'down'
                }`}
              />
            </Button>
          ) : (
            <span />
          )}
        </td>

        <td
          key={`type-${index}`}
          className="nk-tb-col text-left vertical-align-middle pl-2 pl-md-2"
        >
          <div className="d-inline-flex align-items-center">
            <Link
              className="text-primary d-inline-flex align-items-center"
              to={`/detalle-transacciones/${data.societyId}/${operationData.id}`}
            >
              <em className="icon ni ni-link mr-1" />
              {operationData.type === operationTypes.SELL_PARTICIPATION
                ? t('SaleOfShares')
                : t(operationData.alias || operationData.type || '')}
            </Link>

            {operationData.comments && (
              <IconTooltip
                id={`operation-comments-${operationData.id}`}
                tooltip={operationData.comments}
              />
            )}
          </div>
        </td>

        <td
          key={`date-${index}`}
          className="nk-tb-col text-center vertical-align-middle"
        >
          {operationData.date
            ? format(new Date(operationData.date), dateFormat)
            : '-'}
        </td>

        <td
          key={`amount-${index}`}
          className="nk-tb-col tb-col-xl text-right vertical-align-middle"
        >
          {operationData.amount
            ? formatCurrencyDecimals(operationData.amount)
            : '-'}
        </td>

        <td
          key={`premium-${index}`}
          className="nk-tb-col tb-col-xl text-right vertical-align-middle"
        >
          {operationData.premium
            ? formatCurrencyDecimals(operationData.premium)
            : '-'}
        </td>

        <td
          key={`shares-${index}`}
          className="nk-tb-col text-right vertical-align-middle"
        >
          {operationData.sharesCount
            ? formatNumber(operationData.sharesCount)
            : '-'}
        </td>

        <td className="nk-tb-col tb-col-xl text-center vertical-align-middle">
          {operationData.className ? (
            <span>{operationData.className}</span>
          ) : (
            <span>-</span>
          )}
        </td>

        <td className="nk-tb-col tb-col-xl text-right vertical-align-middle">
          {operationData.from ? (
            <span>{operationData.from}</span>
          ) : (
            <span>-</span>
          )}
        </td>

        <td className="nk-tb-col tb-col-xl text-right vertical-align-middle">
          {operationData.to ? <span>{operationData.to}</span> : <span>-</span>}
        </td>

        <DocumentsColumn
          index={index}
          documentsCount={operation.documents.length}
          documents={(operation.documents as unknown as Document[]) || []}
        />

        {data.hasTitlesEnabled &&
          (titleData.operationTitleId ? (
            <DocumentsColumn
              index={index}
              documentsCount={1}
              documents={data.titleDocument ? [data.titleDocument] : []}
              downloadName={downloadTitleName}
              hideOnMobile={false}
              isLandscape={societyShareTitleConfiguration?.isLandscape || false}
              sources={{
                society: actualSociety?._id,
                sharesTitle: titleData.operationTitleId,
              }}
              templateWatermarkText={titleData.titleWatermarkText}
              templateBackgroundImage={
                societyShareTitleConfiguration?.backgroundImage || ''
              }
              templateTextPadding={
                societyShareTitleConfiguration?.textPadding || {}
              }
              tooltipId={`shares-title-document${index}`}
              tooltipText={
                <ShareTitleTooltipText titleNumber={titleData.titleNumber} />
              }
              tooltipPlace="left"
            />
          ) : (
            <td className="nk-tb-col tb-col-xl vertical-align-middle text-center">
              <span>-</span>
            </td>
          ))}
      </tr>

      {data.expandedRows.has(rowNumber) &&
        operationData.partnerTransactions.map(
          (transaction: Transaction, transactionIndex: number) => (
            <PartnerTransactionRow
              key={`transaction-row-${index}`}
              index={transactionIndex}
              transaction={transaction}
              operationType={operationData.type}
            />
          )
        )}
    </>
  );
};

export default PartnerOperationsTableRow;
